import {
  Button,
  LoadingIndicator,
  SlideoutMenu,
  Tabs,
} from "@light-technology/components";
import { ArrowLeft, ArrowRight, Check, Link01 } from "@untitledui/icons";
import { useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { api } from "../../state/api";
import { type AppResponse } from "../../state/api.types";
import { DeleteWebhookModal } from "./DeleteWebhookModal";
import { UpdateWebhookModal } from "./UpdateWebhookModal";
import { WebhookSlideoutDetails } from "./WebhookSlideoutDetails";
import { WebhookSlideoutEvents } from "./WebhookSlideoutEvents";

const tabs = [
  { id: "details", label: "Details" },
  { id: "events", label: "Events" },
];

type WebhookSlideoutProps = {
  app: AppResponse;
  children: ReactNode | ReactNode[];
};

export const WebhookSlideout = (props: WebhookSlideoutProps) => {
  const navigate = useNavigate();
  const { webhookId } = useParams();
  const [tab, setTab] = useState<string | number>(tabs[0].id);
  const [offset, setOffset] = useState(0);

  const getWebhooks = api.useGetWebhooksQuery(props.app.uuid);
  const webhook = getWebhooks.data?.data.find((w) => w.uuid === webhookId);

  const getWebhookEvents = api.useGetWebhookEventsQuery(
    tab === "events" && webhook
      ? { webhookUUID: webhook.uuid, offset }
      : skipToken,
  );

  const close = () => {
    navigate(`/apps/${props.app.slug}/development`);
  };

  return (
    <SlideoutMenu.Trigger
      isOpen={!!webhookId}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          setTab(tabs[0].id);
          setOffset(0);
        } else {
          close();
        }
      }}
    >
      {props.children}
      <SlideoutMenu
        isDismissable
        className="max-w-md"
        dialogClassName="overflow-hidden"
      >
        <Tabs selectedKey={tab} onSelectionChange={setTab}>
          <SlideoutMenu.Header tabs={tabs} onClose={close}>
            {!webhook ? (
              <LoadingIndicator type="brand-spinner" />
            ) : (
              <>
                <div className="flex flex-row items-center gap-2">
                  <div className="text-display-xs font-semibold">Webhook</div>
                  <Button
                    size="sm"
                    color="tertiary"
                    tooltip="Copy shareable link to webhook"
                    tooltipOnClick="Link copied to clipboard"
                    iconLeading={Link01}
                    iconLeadingOnClick={Check}
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                    }}
                  />
                </div>
                <div className="font-mono text-sm text-tertiary">
                  {webhook.uuid}
                </div>
              </>
            )}
          </SlideoutMenu.Header>
          {webhook && (
            <SlideoutMenu.Content className="pt-0">
              <Tabs.Panel id="details" className="pt-3xl">
                <WebhookSlideoutDetails webhook={webhook} />
              </Tabs.Panel>
              <Tabs.Panel id="events" className="-mx-4 pb-3xl md:-mx-6">
                <WebhookSlideoutEvents
                  events={getWebhookEvents.data?.data ?? []}
                  isFetching={getWebhookEvents.isFetching}
                />
              </Tabs.Panel>
            </SlideoutMenu.Content>
          )}
        </Tabs>
        {webhook && (
          <SlideoutMenu.Footer className="flex items-center justify-end gap-lg">
            {tab === "details" && (
              <>
                <DeleteWebhookModal webhook={webhook} onSubmit={close}>
                  <Button color="secondary" size="sm">
                    Delete
                  </Button>
                </DeleteWebhookModal>
                <UpdateWebhookModal webhook={webhook}>
                  <Button color="primary" size="sm">
                    Edit
                  </Button>
                </UpdateWebhookModal>
              </>
            )}
            {tab === "events" && (
              <>
                <Button
                  color="secondary"
                  size="sm"
                  iconLeading={ArrowLeft}
                  isDisabled={getWebhookEvents.isFetching || offset === 0}
                  onClick={() => {
                    if (getWebhookEvents.data) {
                      setOffset(
                        getWebhookEvents.data.offset -
                          getWebhookEvents.data.limit,
                      );
                    }
                  }}
                >
                  Previous
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  iconTrailing={ArrowRight}
                  isDisabled={
                    getWebhookEvents.isFetching ||
                    !getWebhookEvents.data?.has_more
                  }
                  onClick={() => {
                    if (getWebhookEvents.data) {
                      setOffset(
                        getWebhookEvents.data.offset +
                          getWebhookEvents.data.limit,
                      );
                    }
                  }}
                >
                  Next
                </Button>
              </>
            )}
          </SlideoutMenu.Footer>
        )}
      </SlideoutMenu>
    </SlideoutMenu.Trigger>
  );
};
